import { useLocation } from 'wouter-preact'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useState } from 'preact/hooks'
import {
  MainContentLayout,
  Header,
  Content,
  Button,
  Alert,
  InfoBox,
} from '@/components'
import { QrCode } from '@/components'
import { useDeviceDetection } from '@/hooks/useDeviceDetection'
import { getQrCodeData, saveMobileAppAvailable } from '@/services/session'
import { createDeeplinkUrl, createQuerySet } from '@/services/qrCode'
import { InfoBigIcon } from '@/components/icons/InfoBigIcon'
import { usePreventBackNavigation } from '@/services/navigation-guard'
import { useSignProcessStatusPolling } from '@/hooks/useSignProcessStatusPolling'
import { useProtectionRoute } from '@/components/navigation/ProtectedRoute'

export function QrCodePage() {
  const { t } = useTranslation()
  useLocation()

  usePreventBackNavigation()

  const deviceInfo = useDeviceDetection()
  const { isReloadProtectionActive } = useProtectionRoute()
  const { startPolling } = useSignProcessStatusPolling({
    mode: 'qr-code',
  })

  // State for mobile flow
  const [showQrCode, setShowQrCode] = useState(false)
  const [appOpenError, setAppOpenError] = useState<string | null>(null)

  useEffect(() => {
    startPolling()
  }, [])

  const qrCode = getQrCodeData()

  // Enhanced mobile app redirect with error handling
  const handleOpenMobileApp = () => {
    if (!qrCode) return

    const deeplinkWithToken = createDeeplinkUrl(qrCode)

    setAppOpenError(null)

    if (deviceInfo.isMobile) {
      isReloadProtectionActive.current = false
      window.location.href = deeplinkWithToken

      setTimeout(() => {
        if (document.hasFocus()) {
          setAppOpenError(t('qrCode.mobile.appOpenError'))
          setShowQrCode(true)
        } else {
          saveMobileAppAvailable(true)
        }
        isReloadProtectionActive.current = true
      }, 1000)
    } else {
      // Desktop - pokaż QR code
      setShowQrCode(true)
    }
  }

  const handleShowQrCode = () => {
    setShowQrCode(true)
  }

  const parsedQrCode = qrCode ? `eqsig;${createQuerySet(qrCode)}` : ''

  const isMobile = deviceInfo.isMobile && !showQrCode

  return (
    <div className='bg-cencert-bg-gray'>
      <Header />

      <Content showSidebar={true}>
        <MainContentLayout
          title={showQrCode ? t('qrCode.title') : t('qrCode.mobileTitle')}
          currentStep={2}
          totalSteps={4}
          showCTA={false}
          showLegalNotice={false}
        >
          {qrCode ? (
            <div>
              {/* Show error message if app failed to open */}
              {appOpenError && (
                <Alert
                  variant='error'
                  message={appOpenError}
                  onDismiss={() => setAppOpenError(null)}
                  className='mb-6'
                />
              )}

              {/* Info box at the top */}
              {isMobile ? (
                <>
                  <img
                    src='/mobile_icon.png'
                    width={150}
                    className='mx-auto my-8'
                  />
                  <InfoBox
                    description={
                      <Trans
                        i18nKey='qrCode.mobileInstruction'
                        components={{ b: <strong /> }}
                      />
                    }
                  />
                </>
              ) : (
                <div className='bg-sky-50 border border-sky-200 rounded-3xl py-6 px-6 mb-8'>
                  <div className='flex gap-3'>
                    <InfoBigIcon className='w-6 h-6 text-sky-500 flex-shrink-0 mb-3' />
                    <div className='text-gray-700 text-sm leading-relaxed'>
                      <div className='border-gray-200'>
                        <div className='text-sm'>
                          <ol className='list-decimal list-inside space-y-2 text-gray-700'>
                            <li>{t('qrCode.instruction1')}</li>
                            <li>
                              {t('qrCode.instruction2')}{' '}
                              <strong>{t('qrCode.instruction2Bold')}</strong>.
                            </li>
                            <li>
                              {t('qrCode.instruction3')}{' '}
                              <strong>{t('qrCode.instruction3Bold')}</strong>.
                            </li>
                            <li>{t('qrCode.instruction4')}</li>
                            <li>{t('qrCode.instruction5')}</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isMobile ? (
                <div className='flex flex-col gap-4 mt-6 mb-6'>
                  <Button
                    type='primary'
                    size='lg'
                    fullWidth
                    onClick={handleOpenMobileApp}
                  >
                    {t('qrCode.mobile.openApp')}
                  </Button>

                  <Button
                    type='secondary'
                    size='md'
                    fullWidth
                    onClick={handleShowQrCode}
                  >
                    {t('qrCode.mobile.showQrCode')}
                  </Button>
                </div>
              ) : (
                <>
                  {/* QR code section */}
                  <div className='rounded-2xl p-8 mb-8 bg-gray-100'>
                    <QrCode qrCode={parsedQrCode} className='' />
                  </div>
                </>
              )}
            </div>
          ) : (
            <Alert variant='error' message={t('qrCode.noQrCode')} />
          )}
        </MainContentLayout>
      </Content>
    </div>
  )
}
